import { useState } from 'react';
import { Form, Select } from 'antd';
import momentTimezone from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Store } from '../../state/store.interfaces';
import { LrvForm } from '../../common/components/LrvForm/LrvForm';
import { useFormErrorFields } from '../../hooks/useFormErrorFields';
import { LrvModal } from '../../common/components/LrvModal/LrvModal';
import { getUnitPhaseTypeFromAnalysis } from '../../helpers/units.helpers';
import { LrvInputV2 } from '../../common/components/LrvInputV2/LrvInputV2';
import { LrvSelectV2 } from '../../common/components/LrvSelectV2/LrvSelectV2';
import * as onboardingSlice from '../../common/components/Onboarding/OnboardingSlice';
import { getKeyValue, PROVINCES_ECUADOR, unitPhaseTypes } from '../../config/commons';

import styles from './Units.module.scss';
import { createCampus, setSelectedCampus, setShowCreateCampusModal } from './campusSlice';

export const NewUnitForm = () => {
  const [t] = useTranslation();
  const { Option } = Select;
  
  const dispatch = useDispatch();
  const timezones = momentTimezone.tz.names();

  const [form] = Form.useForm();
  const { errorFields, resetErrorFields, extractAndSetErrorFields } = useFormErrorFields({ form });

  const [newCampusName, setNewCampusName] = useState('');
  const [newCampusCode, setNewCampusCode] = useState('');
  const [newCampusTimezone, setNewCampusTimezone] = useState(momentTimezone.tz.guess());
  const [newCampusProvince, setNewCampusProvince] = useState('');

  const {
    showCreateCampusModal,
  } = useSelector((state: Store) => state.campus);

  const { company, phaseType } = useSelector((state: Store) => state.header);
  const unitPhaseType = getUnitPhaseTypeFromAnalysis(phaseType);

  const {
    run: isRunningOnboarding,
  } = useSelector((state: Store) => state.onboarding);

  const [newCampusPhase, setNewCampusPhase] = useState(unitPhaseTypes.LARVAE);

  const MAX_CODES_LENGTH = 4;

  function closeCampusModal () {
    dispatch(setShowCreateCampusModal(false));
    setNewCampusName('');
    setNewCampusCode('');
    setNewCampusTimezone(momentTimezone.tz.guess());
    setNewCampusProvince('');
    setNewCampusPhase(unitPhaseTypes.LARVAE);
    dispatch(setSelectedCampus({ _id: '', name: '', code: '', timezone: '', companyId: '', country: '', province: '', phaseType: '', status: '' }));

    resetErrorFields();
    form.resetFields();
  }

  const childrenTimezone: JSX.Element[] = [];
  for (let i = 0; i < timezones.length; i++) {
    const label = ' (GMT' + momentTimezone.tz(timezones[i]).format('Z') + ') ' + timezones[i];
    childrenTimezone.push(<Option key={timezones[i]} value={timezones[i]}>{label}</Option>);
  }

  const childrenProvinces: JSX.Element[] = [];
  for (let i = 0; i < PROVINCES_ECUADOR.length; i++) {
    const province = PROVINCES_ECUADOR[i];
    childrenProvinces.push(<Option key={province} value={province}>{province}</Option>);
  }

  const renderNewCampusName = () => {
    const hasError = errorFields.includes('newCampusName');

    return (
      <Form.Item
        required
        name='newCampusName'
        rules={[{ required: true, message: t('campus.nameRequired') }]}
      >
        <LrvInputV2
          hasError={hasError}
          label={t('campus.name')}
          theme='light'
          autoFocus
          value={newCampusName}
          onChange={(e) => setNewCampusName(e.target.value)}
        />
      </Form.Item>
    );
  };

  const renderNewCampusCode = () => {
    const hasError = errorFields.includes('newCampusCode');

    return (
      <Form.Item
        required
        name='newCampusCode'
        rules={[{ required: true, whitespace: true, message: t('campus.codeRequired') }]}
      >
        <LrvInputV2
          label={t('campus.code')}
          theme='light'
          value={newCampusCode}
          className={styles.inputUpperCase}
          maxLength={MAX_CODES_LENGTH}
          hasError={hasError}
          onChange={(e) => setNewCampusCode(e.target.value)}
        />
      </Form.Item>
    );
  };

  const renderNewCampusTimezone = () => {
    const hasError = errorFields.includes('newCampusTimezone');

    return (
      <Form.Item
        required
        name='newCampusTimezone'
        rules={[{ required: true }]}
        valuePropName='defaultValue'
        initialValue={momentTimezone.tz.guess()}
      >
        <LrvSelectV2
          label={t('campus.timezone')}
          theme='light'
          showSearch
          value={newCampusTimezone}
          hasError={hasError}
          defaultValue={momentTimezone.tz.guess()}
          onChange={e => setNewCampusTimezone(e)}
        >
          {childrenTimezone}
        </LrvSelectV2>
      </Form.Item>
    );
  };

  const renderNewCampusProvince = () => {
    if (company.isInternational) {
      return;
    }

    const hasError = errorFields.includes('newCampusProvince');

    return (
      <Form.Item
        required
        name='newCampusProvince'
        rules={[{ required: true, message: t('common.requiredField') }]}
      >
        <LrvSelectV2
          label={t('campus.province')}
          theme='light'
          showSearch
          value={newCampusProvince}
          hasError={hasError}
          onChange={e => setNewCampusProvince(e)}
        >
          {childrenProvinces}
        </LrvSelectV2>
      </Form.Item>
    );
  };

  const renderNewCampusPhase = () => {
    const hasError = errorFields.includes('newCampusPhase');

    return (
      <Form.Item
        required
        name='newCampusPhase'
        rules={[{ required: true }]}
        valuePropName='defaultValue'
        initialValue={unitPhaseTypes.LARVAE}
      >
        <LrvSelectV2
          label={t('campus.phase')}
          theme='light'
          showSearch
          className={styles.select}
          value={newCampusPhase}
          onChange={value => setNewCampusPhase(value)}
          hasError={hasError}
        >
          {
            Object.keys(unitPhaseTypes).map((key, index) => {
              const typedKey = key as 'LARVAE' | 'PRODUCTION';

              return (
                <Option key={index} value={getKeyValue(unitPhaseTypes, typedKey)}>{t(`stockings.phaseTypes.${typedKey}`)}</Option>
              );
            })
          }
        </LrvSelectV2>
      </Form.Item>
    );
  };

  return (
    <LrvModal
      theme='light'
      title={t('campus.newCampus')}
      className='newCampusModal'
      open={showCreateCampusModal}
      destroyOnClose={true}
      okButtonProps={{ htmlType: 'submit', form: 'formNewCampus', id: 'formNewCampus_btnSave' }}
      onOk={() => {
        form.submit();
      }}
      okText={t('campus.create')}
      cancelText={t('campus.cancel')}
      onCancel={() => {
        closeCampusModal();
      }}
    >
      <LrvForm
        theme='light'
        form={form}
        layout='vertical'
        name='formNewCampus'
        id='formNewCampus'
        onFinish={() => {
          const campusData = { name: newCampusName, code: newCampusCode, timezone: newCampusTimezone, companyId: company._id, phaseType: newCampusPhase, province: newCampusProvince };
          dispatch(createCampus({ campusData, phaseType: unitPhaseType }));
          closeCampusModal();

          if (isRunningOnboarding) {
            dispatch(onboardingSlice.goToOnboardingNextStep(1000));
          }
        }}
        onFieldsChange={extractAndSetErrorFields}
      >
        {renderNewCampusName()}
        {renderNewCampusCode()}
        {renderNewCampusTimezone()}
        {renderNewCampusProvince()}
        {renderNewCampusPhase()}
      </LrvForm>
    </LrvModal>

  );
};
